import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openUpwards, setOpenUpwards] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 300;

        const shouldOpenUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        setOpenUpwards(shouldOpenUpwards);

        setDropdownPosition({
          top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300;

      const shouldOpenUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setOpenUpwards(shouldOpenUpwards);

      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }

    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <>
      <div ref={containerRef} className={`relative ${className}`}>
        <input type="hidden" name={name} value={value} required={required} />

        <button
          ref={buttonRef}
          type="button"
          id={id}
          onClick={handleToggle}
          className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base cursor-pointer transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 focus:shadow-xl focus:shadow-blue-200/50 font-medium text-left flex items-center justify-between group"
        >
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-blue-500 transition-all duration-300 group-hover:scale-110 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-2xl shadow-2xl shadow-blue-200/30 overflow-hidden animate-fadeIn"
          style={{
            top: openUpwards ? 'auto' : `${dropdownPosition.top}px`,
            bottom: openUpwards ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className="p-3 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
          </div>

          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-5 py-3 text-left hover:bg-blue-50 hover:text-blue-700 transition-colors font-medium ${
                    option.value === value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                  } ${index === 0 ? '' : 'border-t border-gray-100'}`}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-gray-400">
                No options found
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomSelect;
